import { ICar } from "src/api/car";
import { urls } from "src/data/urls/urls.ts";
import { TWarehouseQuery } from "src/types/types";
import { quantitySort } from "src/utils/quantity-sort";
import { StateCreator } from "zustand";
import { CarByNewApi } from "../api/car-by-new-api.ts";
import { WarehouseByNewApi } from "../api/warehouse-by-new-api.ts";
import { Status } from "../enums/status.enum.ts";
import { mapCarByNewApiToCar, statusToStatusIdMap } from "../utils/utils.ts";

const carDataInitState: ICar = {
  carNumber: "",
  carsQuantity: null,
  gateId: null,
  gateName: null,
  queueId: NaN,
  queueNumber: NaN,
  statusId: undefined,
  statusName: "",
  warehouseId: NaN,
  warehouseName: "",
};

export interface WarehousesSlice {
  houseData: ICar[] | [] | null;
  houseName: string | undefined;
  carNumber: string;
  carData: ICar;
  houseIdListForNewApi: number[];
  houseIdListForNewApiLoaded: boolean;
  setHouseData: (houseData: ICar[] | [] | null) => void;
  setHouseName: (name: string | undefined) => void;
  setCarNumber: (number: string) => void;
  setCarData: (carData: ICar | undefined) => void;
  fetchHouseData: (query: TWarehouseQuery, byNewApi: boolean) => void;
  fetchCarData: (id: string, byNewApi: boolean) => void;
  fetchHouseIdListForNewApi: () => void;
}

export const createWarehousesSlice: StateCreator<WarehousesSlice> = set => ({
  houseData: null,
  houseName: "",
  carNumber: "",
  carData: carDataInitState,
  houseIdListForNewApi: [],
  houseIdListForNewApiLoaded: false,
  fetchHouseData: async (query: TWarehouseQuery, byNewApi: boolean) => {
    try {
      let data: ICar[];
      if (byNewApi) {
        const body = {
          filters: {
            statuses: [
              statusToStatusIdMap[Status.SUPPLIER_QUEUE_STATUS_WAITING],
              statusToStatusIdMap[Status.SUPPLIER_QUEUE_STATUS_AT_GATE],
            ],
            office_id: query.id,
          },
          pagination: {
            page: 0,
            page_size: 100000,
          },
        };
        const res = await fetch(
          `${urls.GET_WAREHOUSE_QUEUE_BY_NEW_API}/suppliers-equeue-tablo/by-filters`,
          {
            method: "POST",
            body: JSON.stringify(body),
          },
        );
        const dataByNewApi: { records: CarByNewApi[] } = await res.json();
        data = dataByNewApi.records.map(mapCarByNewApiToCar);
      } else {
        const res = await fetch(
          `${urls.GET_WAREHOUSE_QUEUE_BY_NEW_API}/suppliers-equeue-tablo/old/warehouse-queue?warehouseId=${query.id}`,
        );
        const dataByNewApi: { records: CarByNewApi[] } = await res.json();
        data = dataByNewApi.records.map(mapCarByNewApiToCar);
      }
      set({
        houseData: data.sort((a: ICar, b: ICar) => quantitySort(a, b)),
        houseName: query.name,
      });
    } catch (e) {
      window.location.href = window.location.origin;
    }
  },
  fetchCarData: async (id: string, byNewApi: boolean) => {
    try {
      if (byNewApi) {
        const res = await fetch(
          `${urls.GET_WAREHOUSE_QUEUE_BY_NEW_API}/suppliers-equeue-tablo/queue/short/by-id/${id}`,
        );
        const dataByNewApi: { record: CarByNewApi } = await res.json();
        const carDataByNewApi: ICar = mapCarByNewApiToCar(dataByNewApi.record);

        if (carDataByNewApi === undefined) {
          window.location.href = window.location.origin;
        }
        set({
          carData: carDataByNewApi,
          houseName: carDataByNewApi.warehouseName,
        });
      } else {
        const res = await fetch(
          `${urls.GET_WAREHOUSE_QUEUE_BY_NEW_API}/suppliers-equeue-tablo/old/waiting-car?car_number=${id.toUpperCase()}`,
        );

        const dataByNewApi: { record: CarByNewApi } = await res.json();
        const data = mapCarByNewApiToCar(dataByNewApi.record);

        if (res.status === 404) {
          const res = await fetch(
            `${urls.GET_WAREHOUSE_QUEUE_BY_NEW_API}/suppliers-equeue-tablo/old/unloaded-car?carNumber=${id.toUpperCase()}`,
          );
          const dataByNewApi: { record: CarByNewApi } = await res.json();
          const data = mapCarByNewApiToCar(dataByNewApi.record);

          if (res.status === 404) {
            window.location.href = window.location.origin;
          } else {
            set({ carData: data, houseName: data.warehouseName });
          }
        } else {
          set({ carData: data, houseName: data.warehouseName });
        }
      }
    } catch (e) {
      window.location.href = window.location.origin;
    }
  },
  fetchHouseIdListForNewApi: async () => {
    try {
      const res = await fetch(
        `${urls.GET_WAREHOUSE_QUEUE_BY_NEW_API}/suppliers-equeue-tablo/offices`,
      );
      const data: { offices: WarehouseByNewApi[] } = await res.json();
      set({
        houseIdListForNewApi: data.offices.map(({ office_id }) => office_id),
        houseIdListForNewApiLoaded: true,
      });
    } catch (e) {
      set({
        houseIdListForNewApi: [],
        houseIdListForNewApiLoaded: true,
      });
    }
  },
  setHouseData: houseData => set(() => ({ houseData })),
  setHouseName: houseName => set(() => ({ houseName })),
  setCarNumber: (carNumber: string) => set(() => ({ carNumber })),
  setCarData: (carData: ICar | undefined) => set(() => ({ carData })),
});
